import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Search, FileText, ChevronRight, Lock } from 'lucide-react';
import { getPortalEmoji } from '../utils/portalEmojis';
import { useAuth } from '../contexts/AuthContext';
import { getUserData } from '../services/accessControlService';
import PortalBackground from '../components/PortalBackground';

/**
 * Templates index — organized by department. All department templates,
 * including Technology, live here; other departments list their template
 * pages here until they get
 * their own department pages.
 */

const DEPARTMENT_SECTIONS = [
  {
    name: 'General',
    emoji: getPortalEmoji('general'),
    accent: 'bg-gray-500',
    text: 'text-gray-600 dark:text-gray-300',
    soft: 'bg-gray-100 dark:bg-gray-700/60',
    templates: [
      { id: 'course', title: 'Courses', description: 'Course templates and materials', emoji: '🎓', route: '/crm/course-templates' },
      { id: 'programs', title: 'Programs', description: 'Business growth programs and roadmaps', emoji: '🧭', route: '/crm/programs-template' },
      { id: 'alacarte', title: 'À La Carte', description: 'À la carte solutions and pricing', emoji: '🛒', route: '/crm/alacartetemplate' },
      { id: 'routing', title: 'Routing', description: 'Routing templates and workflow guides', emoji: '🔀', route: '/crm/routing-template' },
      { id: 'drip-campaigns', title: 'Drip Campaigns', description: 'Lead nurture workflows by pipeline stage', emoji: '💧', route: '/crm/drip-campaigns' },
      { id: 'roadmap', title: 'Roadmap', description: 'Roadmap templates and planning tools', emoji: getPortalEmoji('roadmap'), route: '/crm/roadmap-templates' },
      { id: 'branding', title: 'Branding', description: 'BBA brand kit: logos, colors, fonts, voice', emoji: '✨', route: '/crm/templates/branding', ownerOnly: true }
    ]
  },
  {
    name: 'Sales',
    emoji: getPortalEmoji('sales'),
    accent: 'bg-red-600',
    text: 'text-red-600 dark:text-red-400',
    soft: 'bg-red-50 dark:bg-red-900/30',
    templates: [
      { id: 'sales-materials', title: 'Sales Materials', description: 'Sales materials and documentation', emoji: '💼', route: '/crm/collection-sales' }
    ]
  },
  {
    name: 'Marketing',
    emoji: getPortalEmoji('marketing'),
    accent: 'bg-orange-500',
    text: 'text-orange-600 dark:text-orange-400',
    soft: 'bg-orange-50 dark:bg-orange-900/30',
    templates: [
      { id: 'marketing-access', title: 'Access & Logins', description: 'Getting-started access templates per channel', emoji: '🔑', route: '/crm/marketing-access-templates' }
    ]
  },
  {
    name: 'Operations',
    emoji: getPortalEmoji('operations'),
    accent: 'bg-blue-600',
    text: 'text-blue-600 dark:text-blue-400',
    soft: 'bg-blue-50 dark:bg-blue-900/30',
    templates: [
      { id: 'sops', title: 'SOPs', description: 'Standard Operating Procedures', emoji: '📋', route: '/crm/sop-templates' },
      { id: 'jobdesc', title: 'Job Descriptions', description: 'Role and position descriptions', emoji: '🧑‍💼', route: '/crm/job-description-templates' },
      { id: 'kpis', title: 'KPIs', description: 'Key Performance Indicator tracking', emoji: '📊', route: '/crm/kpi-templates' },
      { id: 'operations-materials', title: 'Operations Materials', description: 'Operations materials and documentation', emoji: '📂', route: '/crm/collection-operations' }
    ]
  },
  {
    name: 'Technology',
    emoji: getPortalEmoji('technology'),
    accent: 'bg-purple-600',
    text: 'text-purple-600 dark:text-purple-400',
    soft: 'bg-purple-50 dark:bg-purple-900/30',
    templates: [
      { id: 'techstack', title: 'Tech Stack', description: 'Portal software catalog', emoji: '🖥️', route: '/crm/tech-stack-template' },
      { id: 'automation', title: 'Automation', description: 'Automation templates and workflows', emoji: '⚡', route: '/crm/automation-templates' },
      { id: 'agents', title: 'Agents', description: 'AI agents with routines, abilities, and skills', emoji: '🤖', route: '/crm/agent-templates' },
      { id: 'claude-skills', title: 'Skills', description: 'Skill files for the portal', emoji: '✨', route: '/crm/claude-skills-template' },
      { id: 'imports', title: 'JobTread Imports', description: 'Cost groups, schedules, dashboards, to-dos', emoji: getPortalEmoji('jobtread'), route: '/crm/templates/imports' },
      { id: 'plugins', title: 'Website Plugins', description: 'Plugin licenses and deployments', emoji: '🔌', route: '/crm/templates/plugins' }
    ]
  },
  {
    name: 'HR',
    emoji: getPortalEmoji('hr'),
    accent: 'bg-yellow-500',
    text: 'text-yellow-600 dark:text-yellow-400',
    soft: 'bg-yellow-50 dark:bg-yellow-900/30',
    templates: [
      { id: 'hr-materials', title: 'HR Materials', description: 'HR materials and documentation', emoji: '📦', route: '/crm/collection-hr' }
    ]
  },
  {
    name: 'Finance',
    emoji: getPortalEmoji('finance'),
    accent: 'bg-green-600',
    text: 'text-green-600 dark:text-green-400',
    soft: 'bg-green-50 dark:bg-green-900/30',
    templates: [
      { id: 'finance-materials', title: 'Finance Materials', description: 'Finance materials and documentation', emoji: '💰', route: '/crm/collection-finance' }
    ]
  }
];

const Templates = () => {
  const { currentUser } = useAuth();
  const [searchQuery, setSearchQuery] = useState('');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [isOwner, setIsOwner] = useState(false);

  // `ownerOnly` cards (Branding) are hidden from Executives.
  useEffect(() => {
    if (!currentUser) return;
    let cancelled = false;
    getUserData(currentUser.uid).then((userData) => {
      if (!cancelled) setIsOwner(userData?.userType === 'internal' && userData.role === 'Owner');
    });
    return () => {
      cancelled = true;
    };
  }, [currentUser]);

  const q = searchQuery.trim().toLowerCase();

  const sections = DEPARTMENT_SECTIONS.filter(
    (section) => departmentFilter === 'all' || section.name === departmentFilter
  )
    .map((section) => {
      const visible = section.templates.filter((t) => !t.ownerOnly || isOwner);
      return {
        ...section,
        templates: q
          ? visible.filter(
              (t) =>
                t.title.toLowerCase().includes(q) ||
                t.description.toLowerCase().includes(q) ||
                section.name.toLowerCase().includes(q)
            )
          : visible
      };
    })
    .filter((section) => section.templates.length > 0);

  const inputBase =
    'rounded-xl border border-gray-200 dark:border-gray-700 text-sm bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 shadow-sm focus:outline-none focus:ring-2 focus:ring-red-500 focus:border-transparent';

  return (
    <div className="min-h-screen p-6 relative">
      <PortalBackground />
      <div className="relative max-w-6xl mx-auto">
        <h1 className="text-3xl font-bold tracking-tight text-gray-900 dark:text-white mb-6 text-center">Templates</h1>

        {/* Search + department filter */}
        <div className="flex flex-col sm:flex-row gap-3 mb-8">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              className={`w-full pl-10 pr-3 py-3 ${inputBase}`}
              placeholder="Search templates..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>
          <select
            value={departmentFilter}
            onChange={(e) => setDepartmentFilter(e.target.value)}
            className={`sm:w-56 px-3 py-3 ${inputBase}`}
          >
            <option value="all">All departments</option>
            {DEPARTMENT_SECTIONS.map((section) => (
              <option key={section.name} value={section.name}>
                {section.emoji} {section.name}
              </option>
            ))}
          </select>
        </div>

        {/* Department sections */}
        <div className="space-y-10">
          {sections.map((section) => (
            <section key={section.name}>
              <div className="flex items-center justify-center gap-3 mb-5">
                <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
                <span className={`shrink-0 w-10 h-10 rounded-xl ${section.soft} flex items-center justify-center`}>
                  <span className="text-xl leading-none">{section.emoji}</span>
                </span>
                <h2 className="text-xl md:text-2xl font-bold tracking-tight leading-tight text-center text-gray-900 dark:text-white">
                  {section.name}
                </h2>
                <span className="h-px flex-1 max-w-[110px] bg-gray-200 dark:bg-gray-700" />
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                {section.templates.map((template) => (
                  <Link
                    key={template.id}
                    to={template.route}
                    className="group bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm hover:shadow-md transition-all duration-200 hover:-translate-y-0.5 p-5"
                  >
                    <div className="flex flex-col items-center text-center">
                      <div className={`w-11 h-11 rounded-xl ${section.soft} flex items-center justify-center mb-3`}>
                        <span className="text-xl leading-none">{template.emoji}</span>
                      </div>
                      <p className="text-base font-semibold text-gray-900 dark:text-white inline-flex items-center gap-1.5">
                        {template.title}
                        {template.ownerOnly && <Lock className="w-3.5 h-3.5 text-amber-500" aria-label="Owner only" />}
                      </p>
                      <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                        {template.description}
                      </p>
                      <span className={`mt-2 inline-flex items-center text-xs font-medium ${section.text} opacity-0 group-hover:opacity-100 transition-opacity`}>
                        Manage
                        <ChevronRight className="w-3.5 h-3.5 ml-0.5" />
                      </span>
                    </div>
                  </Link>
                ))}
              </div>
            </section>
          ))}
        </div>

        {/* Empty State */}
        {sections.length === 0 && (
          <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-800 shadow-sm p-8 text-center mt-6">
            <div className="mb-4 mx-auto w-12 h-12 rounded-full bg-gray-100 dark:bg-gray-700 flex items-center justify-center">
              <FileText className="h-6 w-6 text-gray-400" />
            </div>
            <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-1">No templates found</h3>
            <p className="text-gray-500 dark:text-gray-400">Try adjusting your search to find what you're looking for.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Templates;
